import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { useApp } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Textarea } from "@/components/ui/textarea";
import {
  Clock, XCircle, CheckCircle, Loader2, ChefHat,
  Package, ArrowLeft, WifiOff, UtensilsCrossed, Truck,
  Phone, MapPin, RotateCcw, Star, Sparkles, Navigation
} from "lucide-react";

/* ─── Status Config ─── */
const STATUS_CONFIG = {
  waiting_for_confirmation: {
    label: "Väntar på restaurangen",
    sublabel: "Vi har tagit emot din beställning",
    color: "text-[#F59E0B]", bgLight: "bg-[#F59E0B]/10 border-[#F59E0B]/30",
    icon: Clock, step: 1, animate: true,
  },
  accepted: {
    label: "Accepterad!",
    sublabel: "Din order är bekräftad – betala vid avhämtning/leverans",
    color: "text-[#3B82F6]", bgLight: "bg-[#3B82F6]/10 border-[#3B82F6]/30",
    icon: CheckCircle, step: 2, animate: false,
  },
  rejected: {
    label: "Avvisad",
    sublabel: "Tyvärr kunde restaurangen inte ta emot din order",
    color: "text-[#EF4444]", bgLight: "bg-[#EF4444]/10 border-[#EF4444]/30",
    icon: XCircle, step: -1, animate: false,
  },
  paid: {
    label: "Bekräftad",
    sublabel: "Restaurangen förbereder din beställning",
    color: "text-[#3B82F6]", bgLight: "bg-[#3B82F6]/10 border-[#3B82F6]/30",
    icon: CheckCircle, step: 2, animate: false,
  },
  preparing: {
    label: "Tillagas nu",
    sublabel: "Kocken jobbar på din beställning",
    color: "text-[#A855F7]", bgLight: "bg-[#A855F7]/10 border-[#A855F7]/30",
    icon: ChefHat, step: 3, animate: true,
  },
  ready: {
    label: "Redo att hämtas! 🎉",
    sublabel: "Din order väntar på dig",
    color: "text-[#22C55E]", bgLight: "bg-[#22C55E]/10 border-[#22C55E]/30",
    icon: Package, step: 4, animate: true,
  },
  completed: {
    label: "Klar",
    sublabel: "Tack för din beställning!",
    color: "text-[#A8ACB4]", bgLight: "bg-[#6B7280]/10 border-[#6B7280]/30",
    icon: CheckCircle, step: 5, animate: false,
  },
  cancelled: {
    label: "Avbruten",
    sublabel: "Beställningen har avbrutits",
    color: "text-[#EF4444]", bgLight: "bg-[#EF4444]/10 border-[#EF4444]/30",
    icon: XCircle, step: -1, animate: false,
  },
};

const STEPS = [
  { key: "waiting_for_confirmation", label: "Beställd", icon: UtensilsCrossed },
  { key: "accepted", label: "Bekräftad", icon: CheckCircle },
  { key: "preparing", label: "Tillagas", icon: ChefHat },
  { key: "ready", label: "Redo", icon: Package },
  { key: "completed", label: "Klar", icon: CheckCircle },
];

/* ─── Confetti Overlay ─── */
function ConfettiOverlay({ show }) {
  const prefersReducedMotion = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  const pieces = useMemo(() => {
    const viewportW = typeof window !== "undefined" ? window.innerWidth : 1280;
    const viewportH = typeof window !== "undefined" ? window.innerHeight : 800;
    const area = viewportW * viewportH;
    const lowEndDevice = (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4)
      || (navigator.deviceMemory && navigator.deviceMemory <= 4);
    const baseCount = Math.round(area / 85000);
    const count = Math.max(14, Math.min(lowEndDevice ? 22 : 30, baseCount));
    const fallDistance = viewportH + 220;

    const colors = ["#D4B483", "#10B981", "#3B82F6", "#F59E0B", "#A855F7", "#22C55E"];
    return Array.from({ length: count }, (_, i) => {
      const size = 5 + Math.random() * 5;
      const duration = 2300 + Math.random() * 800;
      return {
        id: i,
        left: Math.random() * 100,
        color: colors[Math.floor(Math.random() * colors.length)],
        size,
        rotateStart: -40 + Math.random() * 80,
        rotateEnd: 260 + Math.random() * 220,
        drift: -40 + Math.random() * 80,
        fallDistance,
        duration,
        delay: Math.random() * 420,
      };
    });
  }, []);

  if (!show || prefersReducedMotion) return null;
  // Portaled to <body> so no ancestor filter/transform can turn the
  // fixed confetti layer into an absolutely-positioned one.
  return createPortal(
    <div className="confetti-layer" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="confetti-piece"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size * 1.7}px`,
            backgroundColor: p.color,
            animationDuration: `${p.duration}ms`,
            animationDelay: `${p.delay}ms`,
            "--drift": `${p.drift}px`,
            "--fall-y": `${p.fallDistance}px`,
            "--r0": `${p.rotateStart}deg`,
            "--r1": `${p.rotateEnd}deg`,
          }}
        />
      ))}
    </div>,
    document.body
  );
}

function getConfettiLifetimeMs() {
  return 4200;
}

/* ─── Main Component ─── */
export default function OrderTrackingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, addToCart, t } = useApp();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [connected, setConnected] = useState(false);
  const [statusChanged, setStatusChanged] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [restaurantPhone, setRestaurantPhone] = useState("");
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const wsRef = useRef(null);
  const prevStatusRef = useRef(null);
  const audioCtxRef = useRef(null);
  const isFirstLoad = useRef(true);

  const playStatusSound = (status) => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      if (status === "rejected" || status === "cancelled") {
        osc.frequency.value = 300; osc.type = "sawtooth";
      } else if (status === "ready") {
        osc.frequency.value = 880; osc.type = "sine";
      } else {
        osc.frequency.value = 660; osc.type = "sine";
      }
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
      if (status === "ready") {
        setTimeout(() => {
          const o2 = ctx.createOscillator();
          const g2 = ctx.createGain();
          o2.connect(g2); g2.connect(ctx.destination);
          o2.frequency.value = 1100; o2.type = "sine";
          g2.gain.setValueAtTime(0.2, ctx.currentTime);
          g2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
          o2.start(ctx.currentTime); o2.stop(ctx.currentTime + 0.5);
        }, 150);
      }
    } catch (e) { /* ignore */ }
  };

  const fetchOrder = async () => {
    try {
      const res = await api.get(`/orders/${id}`);
      const newOrder = res.data;

      // Detect status change
      if (prevStatusRef.current && prevStatusRef.current !== newOrder.status) {
        const cfg = STATUS_CONFIG[newOrder.status];
        if (cfg) {
          toast(cfg.label, { description: cfg.sublabel, duration: 5000 });
          playStatusSound(newOrder.status);
          setStatusChanged(true);
          setTimeout(() => setStatusChanged(false), 2000);
          if (document.hidden && "Notification" in window && Notification.permission === "granted") {
            new Notification("Bästa Biten", { body: cfg.label });
          }
        }
      }

      // Confetti on first load if order just placed
      if (isFirstLoad.current && newOrder.status === "waiting_for_confirmation") {
        const age = Date.now() - new Date(newOrder.created_at).getTime();
        if (age < 5000) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), getConfettiLifetimeMs());
        }
        isFirstLoad.current = false;
      }

      prevStatusRef.current = newOrder.status;
      setOrder(newOrder);
    } catch (e) {
      toast.error(t("Order not found"));
      navigate("/orders");
    } finally {
      setLoading(false);
    }
  };

  // Fetch restaurant phone
  useEffect(() => {
    api.get("/menu/restaurant-info").then(res => {
      if (res.data?.phone) setRestaurantPhone(res.data.phone);
    }).catch(() => {});
  }, []);

  // Fetch recommendations when order is rejected
  useEffect(() => {
    if (order?.status !== "rejected") return;

    Promise.all([
      api.get("/menu/products"),
      api.get("/menu/categories"),
    ]).then(([prodRes, catRes]) => {
      const allProducts = prodRes.data || [];
      const categories = catRes.data || [];

      // Categories to exclude entirely from recommendations
      const NON_MEAL_NAMES = ["dryck", "sås", "sauce", "drink"];
      const nonMealIds = new Set(
        categories
          .filter(c => NON_MEAL_NAMES.some(n => c.name.toLowerCase().includes(n)))
          .map(c => c.id)
      );
      // Low-priority snack categories (only shown as last resort)
      const SNACK_NAMES = ["småplock"];
      const snackIds = new Set(
        categories
          .filter(c => SNACK_NAMES.some(n => c.name.toLowerCase().includes(n)))
          .map(c => c.id)
      );

      // Map product_id → full product for quick lookup
      const productMap = Object.fromEntries(allProducts.map(p => [p.id, p]));

      // Find full product objects for what was ordered
      const orderedIds = new Set((order.items || []).map(i => i.product_id));
      const orderedProducts = (order.items || [])
        .map(i => productMap[i.product_id])
        .filter(Boolean);

      const orderedCategoryIds = new Set(orderedProducts.map(p => p.category_id));

      // Build a combined text blob from ordered items (name + ingredient names)
      const orderedText = orderedProducts.map(p =>
        [p.name, ...(p.ingredients || []).map(ing => ing.name || "")].join(" ")
      ).join(" ").toLowerCase();

      const PROTEIN_GROUPS = [
        { keywords: ["biff", "kött", "beef"], tag: "beef" },
        { keywords: ["kyckling", "chicken"], tag: "chicken" },
        { keywords: ["räk", "shrimp", "prawn"], tag: "shrimp" },
        { keywords: ["lax", "salmon", "fisk", "fish"], tag: "fish" },
        { keywords: ["fläsk", "pork", "bacon"], tag: "pork" },
        { keywords: ["vegetar", "vegan", "tofu", "vegoa"], tag: "veggie" },
      ];

      const orderedProteins = new Set(
        PROTEIN_GROUPS.filter(g => g.keywords.some(k => orderedText.includes(k))).map(g => g.tag)
      );

      const getProteinTag = (product) => {
        const text = [product.name, ...(product.ingredients || []).map(ing => ing.name || "")]
          .join(" ").toLowerCase();
        return PROTEIN_GROUPS.find(g => g.keywords.some(k => text.includes(k)))?.tag || null;
      };

      const scoreProduct = (product) => {
        if (orderedIds.has(product.id)) return -1;         // already ordered
        if (nonMealIds.has(product.category_id)) return -1; // drinks/sauces

        const sameCat = orderedCategoryIds.has(product.category_id);
        const proteinMatch = orderedProteins.size > 0 && orderedProteins.has(getProteinTag(product));
        const isSnack = snackIds.has(product.category_id);

        if (sameCat && proteinMatch) return 4; // same category + same protein
        if (sameCat) return 3;                 // same category, different protein
        if (proteinMatch && !isSnack) return 2; // different meal category, same protein
        if (!isSnack) return 1;                // other meal category
        return 0;                              // snacks — absolute last resort
      };

      const scored = allProducts
        .map(p => ({ product: p, score: scoreProduct(p) }))
        .filter(x => x.score >= 0)
        .sort((a, b) => b.score !== a.score ? b.score - a.score : 0.5 - Math.random());

      setRecommendations(scored.slice(0, 3).map(x => x.product));
    }).catch(() => {});
  }, [order?.status, order?.items]);

  useEffect(() => {
    if (!token) { navigate("/auth", { state: { from: `/order/${id}` } }); return; }
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();

    fetchOrder();
    const interval = setInterval(fetchOrder, 5000);

    try {
      const wsUrl = BACKEND_URL.replace("https", "wss").replace("http", "ws");
      const restId = localStorage.getItem("biten_restaurant_id") || "olofstrom";
      const ws = new WebSocket(`${wsUrl}/api/ws/order_${id}?token=${encodeURIComponent(token)}&restaurant=${encodeURIComponent(restId)}`);
      ws.onopen = () => setConnected(true);
      ws.onclose = () => setConnected(false);
      ws.onerror = () => setConnected(false);
      ws.onmessage = (e) => {
        const data = JSON.parse(e.data);
        if (data.type === "status_update") fetchOrder();
      };
      wsRef.current = ws;
    } catch (e) { /* fallback to polling */ }

    return () => { clearInterval(interval); wsRef.current?.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.post(`/orders/${id}/cancel`);
      toast.success(t("Order cancelled"));
      fetchOrder();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Could not cancel"));
    } finally {
      setCancelling(false);
    }
  };

  const handleSubmitReview = async () => {
    if (reviewRating === 0) { toast.error(t("Select number of stars")); return; }
    setSubmittingReview(true);
    try {
      const res = await api.post(`/orders/${id}/review`, { rating: reviewRating, comment: reviewComment });
      setOrder(prev => ({ ...prev, review: res.data.review }));
      toast.success(t("Thanks for your review!"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Could not submit review"));
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleReorder = async () => {
    if (!order) return;

    try {
      const productsRes = await api.get("/menu/products");
      const activeProducts = productsRes.data || [];
      const byId = new Map(activeProducts.map((p) => [p.id, p]));

      let added = 0;
      const missingNames = [];

      order.items.forEach(item => {
        const product = byId.get(item.product_id);
        if (!product) {
          missingNames.push(item.product_name || "Produkt");
          return;
        }

        // Add as base product so reorder never breaks if old modifiers/ingredient IDs changed.
        // Customer can adjust options from menu if needed.
        addToCart({
          product,
          quantity: item.quantity,
          modifiers: [],
          removedIngredients: [],
        });
        added++;
      });

      if (added > 0 && missingNames.length === 0) {
        toast.success(`${added} ${t("items added to cart")}`);
        return;
      }

      if (added > 0 && missingNames.length > 0) {
        const fallback = activeProducts
          .filter((p) => !order.items.some((it) => it.product_id === p.id))
          .slice(0, 3)
          .map((p) => p.name)
          .join(", ");

        toast.warning(
          `${missingNames.length} ${t("products no longer available")}. ${t("We added the rest to your cart.")}`
        );
        if (fallback) {
          toast.message(`${t("Try instead:")} ${fallback}`);
        }
        navigate("/");
        return;
      }

      // None could be reordered.
      const fallback = activeProducts.slice(0, 3).map((p) => p.name).join(", ");
      toast.error(t("These products are no longer on the menu."));
      if (fallback) {
        toast.message(`${t("Try instead:")} ${fallback}`);
      }
      navigate("/");
    } catch (e) {
      toast.error(t("Could not reorder right now"));
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-lg mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }

  if (!order) return null;

  const config = STATUS_CONFIG[order.status] || STATUS_CONFIG.waiting_for_confirmation;
  const currentStep = config.step;
  const StatusIcon = config.icon;
  const isTerminal = ["completed", "rejected", "cancelled"].includes(order.status);
  const isDelivery = order.order_type === "delivery";

  return (
    <div className="p-4 md:p-8 max-w-xl mx-auto relative z-[1]" data-testid="order-tracking-page">
      <ConfettiOverlay show={showConfetti} />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>

        {/* ─ Top bar ─ */}
        <div className="flex items-center justify-between mb-4">
          <Link to="/orders" className="flex items-center gap-1.5 text-[#D4D4D8] hover:text-[#EDEDED] text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t("My Orders")}
          </Link>
          <div className="flex items-center gap-3">
            {restaurantPhone && (
              <a href={`tel:${restaurantPhone}`} className="flex items-center gap-1 text-[#D4B483] hover:text-[#E5CFA5] text-xs transition-colors">
                <Phone className="w-3.5 h-3.5" /> {t("Call us")}
              </a>
            )}
            {!isTerminal && (
              connected ? (
                <div className="flex items-center gap-1.5 text-[#10B981] text-[10px] font-medium">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]" />
                  </span>
                  LIVE
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-[#A8ABB2] text-[10px] font-medium">
                  <WifiOff className="w-3 h-3" /> Polling
                </div>
              )
            )}
          </div>
        </div>

        {/* ─ Main Status Card ─ */}
        <AnimatePresence mode="wait">
          <motion.div
            key={order.status}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.35 }}
            className={`relative overflow-hidden rounded-2xl border p-7 md:p-8 mb-5 text-center ${config.bgLight} ${statusChanged ? "ring-2 ring-[#D4B483] ring-offset-2 ring-offset-[#0A0A0A]" : ""}`}
          >
            <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-64 h-32 bg-[#D4B483]/10 rounded-full blur-3xl pointer-events-none" />
            <span className="lux-eyebrow justify-center relative">{t("Orderstatus")}</span>
            <motion.div
              animate={config.animate ? { scale: [1, 1.12, 1] } : {}}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="flex justify-center mt-4 mb-3 relative"
            >
              <StatusIcon className={`w-12 h-12 ${config.color}`} />
            </motion.div>
            <h2 className={`font-playfair text-3xl md:text-4xl mb-1.5 lux-title relative ${config.color}`}>{config.label}</h2>
            <p className="text-[#D4D4D8] text-sm italic font-light relative">{config.sublabel}</p>

            {/* Estimated time range */}
            {order.estimated_time && (order.status === "accepted" || order.status === "preparing") && (
              <div className="mt-5 inline-flex items-center gap-2.5 rounded-full border border-[#D4B483]/30 bg-[#0A0A0A]/60 px-5 py-2.5 relative">
                <Clock className="w-4 h-4 text-[#D4B483]" />
                <span className="font-mono-price text-gold font-bold text-sm tracking-wide">{order.estimated_time} min</span>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* ─ Order number + type ─ */}
        <div className="flex items-center justify-between mb-4 px-1">
          <div>
            <p className="text-[#A8ABB2] text-[10px] uppercase tracking-widest font-semibold">{t("Order")}</p>
            <p className="text-[#EDEDED] font-bold text-lg">{order.order_number}</p>
          </div>
          <Badge className={`border-0 text-xs ${isDelivery ? "bg-[#3B82F6]/15 text-[#3B82F6]" : "bg-[#10B981]/15 text-[#10B981]"}`}>
            {isDelivery ? <><Truck className="w-3 h-3 mr-1" /> {t("Delivery")}</> : <><Package className="w-3 h-3 mr-1" /> {t("Pickup")}</>}
          </Badge>
        </div>

        {/* ─ Delivery address ─ */}
        {isDelivery && order.delivery_address && (
          <div className="lux-surface rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-[#3B82F6] mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[#A8ABB2] text-[10px] uppercase tracking-wider font-semibold mb-0.5">{t("Delivery address")}</p>
                <p className="text-[#EDEDED] text-sm">
                  {order.delivery_address.street}
                  {order.delivery_address.apartment && `, ${order.delivery_address.apartment}`}
                </p>
                <p className="text-[#D4D4D8] text-xs mb-2">
                  {order.delivery_address.postal_code} {order.delivery_address.city}
                </p>
                <button
                  onClick={() => {
                    const dest = [
                      order.delivery_address.street,
                      order.delivery_address.postal_code || order.delivery_address.zip_code,
                      order.delivery_address.city,
                    ].filter(Boolean).join(", ");
                    const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent("Östra Storgatan 2, Olofström")}&destination=${encodeURIComponent(dest)}&travelmode=driving`;
                    window.open(url, "_blank", "noopener,noreferrer");
                  }}
                  className="inline-flex items-center gap-1.5 text-[#D4B483] hover:text-[#E5CFA5] text-xs font-medium transition-colors"
                >
                  <Navigation className="w-3.5 h-3.5" />
                  {t("Show on map")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─ Progress Steps — gilded timeline ─ */}
        {currentStep > 0 && (
          <div className="lux-surface rounded-2xl px-5 pt-6 pb-5 mb-4">
            <p className="text-center text-[9px] uppercase tracking-[0.38em] text-[#A8ABB2] mb-6 select-none">
              {t("Din beställnings resa")}
            </p>
            <div className="relative">
              {/* Track */}
              <div className="absolute top-[18px] left-[10%] right-[10%] h-px bg-[#27272A]" />
              {/* Gilded progress */}
              <motion.div
                initial={false}
                animate={{ width: `${Math.max(0, ((currentStep - 1) / (STEPS.length - 1)) * 80)}%` }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="absolute top-[18px] left-[10%] h-px max-w-[80%] bg-gradient-to-r from-[#B18B52] via-[#D4B483] to-[#EFDFBC] shadow-[0_0_14px_rgba(212,180,131,0.6)]"
              />
              <div className="relative flex justify-between">
                {STEPS.map((step, i) => {
                  const stepNum = i + 1;
                  const isActive = currentStep >= stepNum;
                  const isCurrent = currentStep === stepNum;
                  const StepIcon = step.icon;
                  return (
                    <div key={step.key} className="flex flex-col items-center" style={{ width: "20%" }}>
                      <motion.div
                        animate={isCurrent ? { scale: [1, 1.08, 1] } : {}}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                        className={`relative w-9 h-9 rounded-full flex items-center justify-center z-10 transition-all duration-500 ${
                          isActive
                            ? "bg-gradient-to-br from-[#E5CFA5] to-[#C6A664] text-[#0A0A0A] shadow-[0_6px_22px_rgba(212,180,131,0.4)]"
                            : "bg-[#121212] text-[#3F3F46] border border-[#27272A]"
                        }`}
                      >
                        {isCurrent && (
                          <span className="absolute inset-0 rounded-full border border-[#D4B483]/60 animate-ping" style={{ animationDuration: "2.2s" }} />
                        )}
                        <StepIcon className="w-4 h-4" />
                      </motion.div>
                      <span className={`mt-2.5 text-[9px] uppercase tracking-[0.14em] font-semibold text-center transition-colors duration-500 ${
                        isCurrent ? "text-gold" : isActive ? "text-[#D4B483]/80" : "text-[#3F3F46]"
                      }`}>
                        {step.label}
                      </span>
                      {isCurrent && (
                        <span className="mt-1 w-1 h-1 rounded-full bg-[#D4B483]" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ─ Rejection reason ─ */}
            {order.status === "rejected" && order.rejection_reason && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-4 mb-4">
            <p className="text-[#EF4444] text-sm font-medium">{t("Reason")}: {order.rejection_reason}</p>
          </motion.div>
        )}

        {/* ─ Recommendations on rejection ─ */}
        {order.status === "rejected" && recommendations.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-4"
          >
            <div className="flex items-center gap-2 mb-3 px-1">
              <Sparkles className="w-4 h-4 text-[#D4B483]" />
              <p className="text-[#EDEDED] text-sm font-semibold">{t("Recommendation")}</p>
            </div>
            <div className="grid grid-cols-3 gap-2.5">
              {recommendations.map((product, i) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 + i * 0.08 }}
                >
                  <Link to={`/product/${product.id}`} className="block group">
                    <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden hover:border-[#D4B483]/40 transition-colors">
                      <div className="overflow-hidden">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-20 object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-2">
                        <p className="text-[#EDEDED] text-[11px] font-medium leading-tight line-clamp-2">{product.name}</p>
                        <p className="text-[#D4B483] text-[11px] font-bold mt-1">{product.base_price} kr</p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* ─ Pay on spot notice ─ */}
        {(order.status === "accepted" || order.status === "paid") && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border border-[#D4B483]/40 bg-[#D4B483]/8 p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[#D4B483]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                {order.order_type === "delivery" ? <Truck className="w-4 h-4 text-[#D4B483]" /> : <Package className="w-4 h-4 text-[#D4B483]" />}
              </div>
              <div className="flex-1">
                <p className="text-[#EDEDED] font-semibold text-sm">
                  {order.order_type === "delivery" ? t("Payment on delivery") : t("Payment on pickup")}
                </p>
                <p className="text-[#D4D4D8] text-xs mt-0.5">
                  {order.order_type === "delivery"
                    ? t("You pay when you receive your delivery")
                    : t("You pay when you pick up your order")}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* ─ Action Buttons ─ */}
        <div className="flex gap-3 mb-4">
          {(order.status === "waiting_for_confirmation" || order.status === "accepted" || order.status === "paid") && (
            <Button onClick={handleCancel} disabled={cancelling} variant="outline"
              className="w-full border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10 h-12 rounded-xl"
              data-testid="cancel-order-btn">
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : t("Cancel order")}
            </Button>
          )}
        </div>

        {/* ─ Order Details / Receipt ─ */}
        <div className={`lux-surface rounded-xl p-5 ${isTerminal ? "relative overflow-hidden" : ""}`}>
          {/* Receipt header for completed orders */}
          {order.status === "completed" && (
            <div className="text-center mb-4 pb-4 border-b border-dashed border-[#27272A]">
              <p className="text-[#A8ABB2] text-[10px] uppercase tracking-[0.2em] font-semibold">{t("Receipt")}</p>
              <p className="text-[#EDEDED] font-playfair text-lg font-bold mt-1">Bästa Biten</p>
              <p className="text-[#A8ABB2] text-[10px] mt-1">
                {new Date(order.created_at).toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </p>
              <p className="text-[#A8ABB2] text-[10px]">Order {order.order_number}</p>
            </div>
          )}

          {order.special_instructions && (
            <div className="mb-4 p-3 rounded-lg bg-[#D4B483]/5 border border-[#D4B483]/20">
              <p className="text-[#D4B483] text-[10px] font-semibold uppercase tracking-wider mb-1">{t("Your note")}</p>
              <p className="text-[#EDEDED] text-sm">{order.special_instructions}</p>
            </div>
          )}

          <h3 className="font-medium text-[#EDEDED] mb-3 text-sm">{t("Items")}</h3>
          <div className="space-y-2">
            {order.items.map((item, i) => (
              <div key={i} className="flex justify-between text-sm">
                <div>
                  <span className="text-[#EDEDED]">{item.quantity}x {item.product_name}</span>
                  {item.selected_options?.length > 0 && (
                    <p className="text-xs text-[#D4D4D8]">{item.selected_options.map(o => o.name).join(", ")}</p>
                  )}
                  {item.modifiers?.length > 0 && (
                    <p className="text-xs text-[#D4B483]/70">{item.modifiers.map(m => m.name).join(", ")}</p>
                  )}
                  {item.removed_ingredients?.length > 0 && (
                    <p className="text-xs text-[#EF4444]/70">{t("Without")}: {item.removed_ingredients.join(", ")}</p>
                  )}
                </div>
                <span className="text-[#EDEDED] flex-shrink-0">{item.item_total.toFixed(0)} kr</span>
              </div>
            ))}
          </div>
          <Separator className="my-3 bg-[#27272A]" />
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-[#D4D4D8]">{t("Subtotal")}</span>
              <span className="text-[#EDEDED]">{order.subtotal.toFixed(0)} kr</span>
            </div>
            {order.delivery_fee > 0 && (
              <div className="flex justify-between">
                <span className="text-[#D4D4D8]">{t("Delivery fee")}</span>
                <span className="text-[#EDEDED]">{order.delivery_fee.toFixed(0)} kr</span>
              </div>
            )}
            {(order.discount_amount || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-[#D4D4D8]">{t("Discount")} {order.promo_code ? `(${order.promo_code})` : ""}</span>
                <span className="text-[#10B981]">- {order.discount_amount.toFixed(0)} kr</span>
              </div>
            )}
            <div className="flex justify-between font-bold pt-1">
              <span className="text-[#EDEDED]">{t("Total")}</span>
              <span className="text-[#D4B483] text-lg">{order.total.toFixed(0)} kr</span>
            </div>
          </div>
        </div>

        {/* ─ Order Review ─ */}
        {(order.status === "completed" || order.status === "ready") && (
          <div className="lux-surface rounded-xl p-5 mt-4">
            {order.review ? (
              <div>
                <p className="text-[#A8ABB2] text-[10px] uppercase tracking-[0.15em] font-semibold mb-2">{t("Your review")}</p>
                <div className="flex items-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} className={`w-5 h-5 ${s <= order.review.rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#27272A]"}`} />
                  ))}
                </div>
                {order.review.comment && (
                  <p className="text-[#EDEDED] text-sm">{order.review.comment}</p>
                )}
              </div>
            ) : (
              <div>
                <p className="text-[#A8ABB2] text-[10px] uppercase tracking-[0.15em] font-semibold mb-1">{t("Rate your order")}</p>
                <p className="text-[#D4D4D8] text-xs mb-3">{t("How was the food? Your rating helps us improve.")}</p>
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map(s => (
                    <button key={s} onClick={() => setReviewRating(s)}
                      onMouseEnter={() => setReviewHover(s)} onMouseLeave={() => setReviewHover(0)}
                      className="p-0.5 transition-transform hover:scale-110">
                      <Star className={`w-7 h-7 transition-colors ${
                        s <= (reviewHover || reviewRating)
                          ? "fill-[#F59E0B] text-[#F59E0B]"
                          : "text-[#27272A] hover:text-[#3F3F46]"
                      }`} />
                    </button>
                  ))}
                </div>
                <Textarea
                  placeholder={t("Leave a comment (optional)...")}
                  value={reviewComment}
                  onChange={e => setReviewComment(e.target.value)}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] placeholder:text-[#A8ABB2] text-sm mb-3 resize-none h-20"
                />
                <Button onClick={handleSubmitReview} disabled={submittingReview || reviewRating === 0}
                  className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold h-10 rounded-xl text-sm">
                  {submittingReview ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
                  {t("Submit review")}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ─ Completed / Terminal Actions ─ */}
        {isTerminal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-4 space-y-3">
            {order.status === "completed" && (
              <Button onClick={handleReorder}
                className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold h-12 rounded-xl">
                <RotateCcw className="w-4 h-4 mr-2" /> {t("Reorder")}
              </Button>
            )}
            <Link to="/">
              <Button variant="outline"
                className="w-full border-[#27272A] text-[#EDEDED] hover:bg-[#1A1A1A] font-semibold h-12 rounded-xl mt-2">
                <UtensilsCrossed className="w-4 h-4 mr-2" /> {t("Back to menu")}
              </Button>
            </Link>
          </motion.div>
        )}

        {/* ─ Contact footer ─ */}
        {!isTerminal && restaurantPhone && (
          <div className="mt-4 text-center">
            <a href={`tel:${restaurantPhone}`}
              className="inline-flex items-center gap-2 text-[#D4D4D8] hover:text-[#EDEDED] text-xs transition-colors">
              <Phone className="w-3.5 h-3.5" /> {t("Problem? Call us:")} {restaurantPhone}
            </a>
          </div>
        )}
      </motion.div>
    </div>
  );
}
